import React, { useState, useEffect, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { DocumentDuplicateIcon } from '@heroicons/react/24/outline';
import { 
  PlusIcon, 
  MagnifyingGlassIcon, 
  PencilIcon, 
  EyeIcon,
  DocumentArrowDownIcon,
  ArrowPathIcon,
  FunnelIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';
import axios from '../../api/axiosConfig';
import { useAuth } from '../../contexts/AuthContext';
import PageHeader from '../../components/common/PageHeader';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import Pagination from '../../components/common/Pagination';
import Select from 'react-select';

interface Structure {
  id: number;
  code: string;
  type: string;
}

interface ClientOption {
  value: number;
  label: string;
  director?: string | null;
}

interface CreatedByUser {
  id: number;
  username: string;
  firstName?: string;
  lastName?: string;
}

interface Order {
  id: number;
  orderNumber: string;
  clientId: number;
  status: 'reserved' | 'waitingPayment' | 'partiallyPaid' | 'fullyPaid' | 'completed' | 'cancelled';
  finalAmount: number;
  paidAmount: number;
  startDate: string;
  endDate: string;
  createdAt: string;
  updatedAt: string;
  orderDate: string;
  Client?: {
    id: number;
    name: string;
  };
  OrderItems?: {
    structureId: number;
    Structure?: Structure;
  }[];
  CreatedBy?: CreatedByUser;
}

const ORDER_STATUSES: { [key: string]: string } = {
  reserved: 'Бронь',
  waitingPayment: 'Ожидает оплаты',
  partiallyPaid: 'Частично оплачен',
  fullyPaid: 'Полностью оплачен',
  completed: 'Завершен',
  cancelled: 'Отменен'
};

const OrderList: React.FC = () => {
  const { can } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [totalOrders, setTotalOrders] = useState(0);
  const [currentPage, setCurrentPage] = useState(parseInt(searchParams.get('page') || '1'));
  const [totalPages, setTotalPages] = useState(1);
  const [limit] = useState(15);

  const [searchTerm, setSearchTerm] = useState(searchParams.get('search') || '');
  const [statusFilter, setStatusFilter] = useState(searchParams.get('status') || 'all');
  const [clientFilter, setClientFilter] = useState<number | null>(searchParams.get('clientId') ? Number(searchParams.get('clientId')) : null);
  const [dateFilter, setDateFilter] = useState(searchParams.get('dateFilter') || 'all');
  const [customStartDate, setCustomStartDate] = useState(searchParams.get('startDate') || '');
  const [customEndDate, setCustomEndDate] = useState(searchParams.get('endDate') || '');
  const [sortBy, setSortBy] = useState(searchParams.get('sortBy') || 'createdAt');
  const [sortOrder, setSortOrder] = useState(searchParams.get('order') || 'DESC');

  const [availableClients, setAvailableClients] = useState<ClientOption[]>([]);
  const [selectedClientOption, setSelectedClientOption] = useState<ClientOption | null>(null);
  const [showFilters, setShowFilters] = useState(false);
  
  useEffect(() => {
    const fetchClients = async () => {
      try {
        const response = await axios.get('/clients', { params: { limit: 1000 } }); 
        const options = response.data.clients.map((c: any) => ({
          value: c.id,
          label: `${c.name} (${c.director || '-'})`,
          director: c.director
        }));
        setAvailableClients(options);
      } catch (error) {
        console.error("Error fetching clients:", error);
      }
    };
    fetchClients();
  }, []);

  const fetchOrders = useCallback(async () => {
    setLoading(true);
    setError(null);

    const params: any = {
      page: currentPage,
      limit,
      sortBy,
      order: sortOrder
    };
    if (searchTerm) params.search = searchTerm;
    if (statusFilter !== 'all') params.status = statusFilter;
    if (clientFilter) params.clientId = clientFilter;
    
    if (dateFilter === 'custom' && customStartDate && customEndDate) {
      params.startDate = customStartDate;
      params.endDate = customEndDate;
    } else if (dateFilter === 'active') {
      const todayStr = new Date().toISOString().split('T')[0];
      params.startDate = todayStr;
      params.endDate = todayStr;
    } else if (dateFilter === 'future') {
      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);
      params.startDate = tomorrow.toISOString().split('T')[0];
    } else if (dateFilter === 'past') {
      const yesterday = new Date();
      yesterday.setDate(yesterday.getDate() - 1);
      params.endDate = yesterday.toISOString().split('T')[0];
    }

    setSearchParams(params, { replace: true });

    try {
      const response = await axios.get('/orders', { params });
      setOrders(response.data.orders);
      setTotalOrders(response.data.totalOrders);
      setTotalPages(response.data.totalPages);
      setCurrentPage(response.data.currentPage);
    } catch (error) {
      console.error('Error fetching orders:', error);
      setError('Ошибка при загрузке заказов');
      setOrders([]);
      setTotalOrders(0);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  }, [currentPage, limit, sortBy, sortOrder, searchTerm, statusFilter, clientFilter, dateFilter, customStartDate, customEndDate, setSearchParams]);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const handleStatusChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setStatusFilter(e.target.value);
    setCurrentPage(1); 
  };

   const handleClientChange = (option: ClientOption | null) => {
      setClientFilter(option ? option.value : null);
      setSelectedClientOption(option);
      setCurrentPage(1);
   };

  const handleDateFilterChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setDateFilter(e.target.value);
    if (e.target.value !== 'custom') {
        setCustomStartDate('');
        setCustomEndDate('');
    }
    setCurrentPage(1); 
  };
  
  const handleCustomDateChange = () => {
     setCurrentPage(1);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleSort = (column: string) => {
      if (sortBy === column) {
          setSortOrder(sortOrder === 'ASC' ? 'DESC' : 'ASC');
      } else {
          setSortBy(column);
          setSortOrder('ASC');
      }
      setCurrentPage(1);
  };

  const formatShortDate = (dateString: string) => {
    if (!dateString) return '-';
    
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('ru-RU', {
      day: 'numeric',
      month: 'numeric',
      year: 'numeric'
    }).format(date);
  };
  
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('ru-KZ', {
      style: 'currency',
      currency: 'KZT',
      maximumFractionDigits: 0
    }).format(amount);
  };
  
  const getStatusText = (status: string) => {
    return ORDER_STATUSES[status] || status;
  };
  
  const getStatusBadgeClass = (status: string) => {
    switch (status) {
      case 'reserved':
        return 'bg-blue-100 text-blue-800';
      case 'waitingPayment':
        return 'bg-yellow-100 text-yellow-800';
      case 'partiallyPaid':
        return 'bg-orange-100 text-orange-800';
      case 'fullyPaid':
        return 'bg-green-100 text-green-800';
      case 'completed':
        return 'bg-purple-100 text-purple-800';
      case 'cancelled':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };
  
  const getPaymentPercentage = (order: Order) => {
    if (order.finalAmount === 0) return 0;
    return Math.min(100, Math.round((order.paidAmount / order.finalAmount) * 100));
  };
  
  const resetFilters = () => {
    setSearchTerm('');
    setStatusFilter('all');
    setClientFilter(null);
    setSelectedClientOption(null);
    setDateFilter('all');
    setCustomStartDate('');
    setCustomEndDate('');
    setSortBy('createdAt');
    setSortOrder('DESC');
    setCurrentPage(1); 
    setShowFilters(false);
    setSearchParams({}, { replace: true });
  };
  
  if (loading) {
    return <LoadingSpinner />;
  }
  
  return (
    <div>
      <PageHeader
        title="Заказы"
        subtitle="Управление текущими и прошлыми заказами"
        actions={
          <div className="flex items-center space-x-2">
            <button onClick={() => setShowFilters(!showFilters)} className="btn-icon">
              <FunnelIcon className="h-5 w-5" />
            </button>
            {can('orders.create') && (
              <Link to="/orders/new" className="btn-primary flex items-center">
                <PlusIcon className="h-5 w-5 mr-1" />
                Создать заказ
              </Link>
            )}
          </div>
        }
      />
      
      {error && (
        <div className="bg-red-100 text-red-700 p-4 rounded-md mb-4">
          {error}
        </div>
      )}
      
      {showFilters && (
        <div className="bg-gray-50 p-4 rounded-lg shadow mb-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <label htmlFor="search" className="form-label">Поиск</label>
              <input id="search" type="text" placeholder="Номер, клиент..." 
                  className="form-input" value={searchTerm} onChange={handleSearchChange} />
            </div>
            <div>
              <label htmlFor="status-filter" className="form-label">Статус</label>
              <select id="status-filter" className="form-input" value={statusFilter} onChange={handleStatusChange}>
                  <option value="all">Все статусы</option>
                  {Object.entries(ORDER_STATUSES).map(([key, label]) => (
                      <option key={key} value={key}>{label}</option>
                  ))}
              </select>
            </div>
            <div>
              <label htmlFor="client-filter" className="form-label">Клиент</label>
              <Select<ClientOption>
                  inputId="client-filter"
                  options={availableClients}
                  value={selectedClientOption}
                  onChange={handleClientChange}
                  placeholder="Все клиенты"
                  isClearable
                  noOptionsMessage={() => 'Клиенты не найдены'}
                  classNamePrefix="react-select"
               />
            </div>
            <div>
              <label htmlFor="date-filter" className="form-label">Период размещения</label>
              <select id="date-filter" className="form-input" value={dateFilter} onChange={handleDateFilterChange}>
                  <option value="all">Все</option>
                  <option value="active">Активные сейчас</option>
                  <option value="future">Будущие</option>
                  <option value="past">Прошедшие</option>
                  <option value="custom">Выбрать диапазон</option>
              </select>
            </div>
            {dateFilter === 'custom' && (
                <>
                    <div className="lg:col-start-2">
                        <label htmlFor="customStartDate" className="form-label">Начало периода</label>
                        <input id="customStartDate" type="date" className="form-input" 
                            value={customStartDate} onChange={(e) => setCustomStartDate(e.target.value)} onBlur={handleCustomDateChange}/>
                    </div>
                    <div>
                        <label htmlFor="customEndDate" className="form-label">Конец периода</label>
                        <input id="customEndDate" type="date" className="form-input" 
                            value={customEndDate} onChange={(e) => setCustomEndDate(e.target.value)} onBlur={handleCustomDateChange}/>
                    </div>
                </>
            )}
          </div>
          <div className="mt-4 flex justify-end">
              <button onClick={resetFilters} className="btn-secondary-outline text-sm flex items-center">
                  <XMarkIcon className="h-4 w-4 mr-1"/> Сбросить фильтры
              </button>
          </div>
        </div>
      )}
      
      <div className="mt-8 flow-root">
        <div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
          <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
            <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 sm:rounded-lg">
              {loading ? (
                <LoadingSpinner />
              ) : error ? (
                <div className="text-center py-4 text-red-500">{error}</div>
              ) : (
                <>
                  <table className="min-w-full divide-y divide-gray-300">
                    <thead className="bg-gray-50">
                      <tr>
                        <th scope="col" className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900" >
                           №
                        </th>
                        <th scope="col" className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6 cursor-pointer hover:bg-gray-100" onClick={() => handleSort('orderNumber')}>
                          Номер {sortBy === 'orderNumber' ? (sortOrder === 'ASC' ? '▲' : '▼') : ''}
                        </th>
                        <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 cursor-pointer hover:bg-gray-100" onClick={() => handleSort('Client.name')}>
                          Клиент {sortBy === 'Client.name' ? (sortOrder === 'ASC' ? '▲' : '▼') : ''}
                        </th>
                        <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 cursor-pointer hover:bg-gray-100" onClick={() => handleSort('status')}>
                          Статус {sortBy === 'status' ? (sortOrder === 'ASC' ? '▲' : '▼') : ''}
                        </th>
                        <th scope="col" className="px-3 py-3.5 text-right text-sm font-semibold text-gray-900 cursor-pointer hover:bg-gray-100" onClick={() => handleSort('finalAmount')}>
                          Сумма заказа {sortBy === 'finalAmount' ? (sortOrder === 'ASC' ? '▲' : '▼') : ''}
                        </th>
                        <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 cursor-pointer hover:bg-gray-100" onClick={() => handleSort('startDate')}>
                          Начало {sortBy === 'startDate' ? (sortOrder === 'ASC' ? '▲' : '▼') : ''}
                        </th>
                        <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 cursor-pointer hover:bg-gray-100" onClick={() => handleSort('endDate')}>
                          Конец {sortBy === 'endDate' ? (sortOrder === 'ASC' ? '▲' : '▼') : ''}
                        </th>
                        <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 cursor-pointer hover:bg-gray-100" onClick={() => handleSort('orderDate')}>
                          Создан {sortBy === 'orderDate' ? (sortOrder === 'ASC' ? '▲' : '▼') : ''}
                        </th>
                        <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 cursor-pointer hover:bg-gray-100" onClick={() => handleSort('CreatedBy.username')}>
                          Ответственный {sortBy === 'CreatedBy.username' ? (sortOrder === 'ASC' ? '▲' : '▼') : ''}
                        </th>
                        <th scope="col" className="relative py-3.5 pl-3 pr-4 sm:pr-6">
                          <span className="sr-only">Действия</span>
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 bg-white">
                      {orders.map((order, index) => (
                        <tr key={order.id} className="hover:bg-gray-50">
                              <td className="py-4 pl-4 pr-3 text-sm text-gray-500 sm:pl-6">
      {(currentPage - 1) * limit + index + 1}
    </td>
                          <td className="py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-6">
                             <Link to={`/orders/${order.id}`} className="text-indigo-600 hover:text-indigo-900">
                                {order.orderNumber || `Заказ #${order.id}`}
                             </Link>
                          </td>
                          {/* Added break-words */}
                          <td className="px-3 py-4 text-sm text-gray-500 break-words">{order.Client?.name || '-'}</td>
                          <td className="px-3 py-4 text-sm text-gray-500">
                              <span className={`inline-flex items-center rounded-md px-2 py-1 text-xs font-medium ring-1 ring-inset ${getStatusBadgeClass(order.status)}`}>
                                {getStatusText(order.status)}
                              </span>
                              {(order.status === 'partiallyPaid' || order.status === 'fullyPaid' || order.status === 'waitingPayment') && order.finalAmount > 0 && (
                                   <div className="w-full bg-gray-200 rounded-full h-1.5 mt-1">
                                     <div 
                                        className="bg-green-600 h-1.5 rounded-full" 
                                        style={{ width: `${getPaymentPercentage(order)}%` }}
                                      ></div>
                                   </div>
                               )}
                          </td>
                          <td className="px-3 py-4 text-sm text-gray-500 text-right">
                            {formatCurrency(order.finalAmount)}
                          </td>
                          <td className="px-3 py-4 text-sm text-gray-500">{formatShortDate(order.startDate)}</td>
                          <td className="px-3 py-4 text-sm text-gray-500">{formatShortDate(order.endDate)}</td>
                          <td className="px-3 py-4 text-sm text-gray-500">{formatShortDate(order.orderDate)}</td>
                          
                          {/* Added break-words */}
                          <td className="px-3 py-4 text-sm text-gray-500 break-words">
                            {order.CreatedBy 
                              ? `${order.CreatedBy.firstName || ''} ${order.CreatedBy.lastName || ''}`.trim() || order.CreatedBy.username
                              : '-'}
                          </td>
                          <td className="relative py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-6">
                            <Link to={`/orders/${order.id}`} className="text-indigo-600 hover:text-indigo-900 mr-3" title="Просмотр">
                              <EyeIcon className="h-5 w-5 inline"/>
                            </Link>
                            {can('orders.edit') && (
                              <Link to={`/orders/edit/${order.id}`} className="text-yellow-600 hover:text-yellow-900 mr-3" title="Редактировать">
                                <PencilIcon className="h-5 w-5 inline"/>
                              </Link>
                            )}
                            {can('orders.create') && (
                              <Link to={`/orders/new?copyFrom=${order.id}`} className="text-blue-600 hover:text-blue-900" title="Копировать">
                                <DocumentDuplicateIcon className="h-5 w-5 inline"/>
                              </Link>
                            )}
                          </td>
                        </tr>
                      ))}
                      {orders.length === 0 && (
                         <tr>
                            <td colSpan={8} className="text-center py-4 text-gray-500">Заказы не найдены.</td>
                         </tr>
                       )}
                    </tbody>
                  </table>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Pagination Controls - Moved here */}
      {totalPages > 1 && (
        <div className="p-4 border-t border-gray-200">
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={handlePageChange}
          />
        </div>
      )}
    </div>
  );
};

export default OrderList;
